// File: apps/web/src/lib/api/import.ts
// Version: 2026.4.7 | Sprint 7 Wave 3 — Import API hooks
//
// PATTERN
//
// Mirrors the existing apps/web/src/lib/api/projects.ts conventions:
//   • useApiClient().createApi() returns an axios instance with the
//     Entra JWT auto-injected.
//   • Read hooks return { data, loading, error, refetch }.
//   • Mutation hooks return { mutate, loading, error } and surface a
//     promise that resolves to the result (or null on failure).
//   • Errors are mapped to a flat ApiError shape so callers don't have
//     to drill into err.response.data.message variants.
//
// All endpoints live under /api/v1/import/* on api.forgetrack.io. The
// preview endpoint is multipart/form-data; everything else is JSON.

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { ApiError } from './types';
import {
  isApiErrorEnvelope,
  buildClientErrorEnvelope,
  type ApiErrorEnvelope,
} from './types-errors';
import type {
  ImportPreviewResponse,
  ImportDispatchResult,
  ImportInFlightConflict,
  StagedListRequest,
  StagedListResponse,
  StagedConversationResponse,
  UpdateMappingRequest,
  CommitRequest,
  CommitResult,
  IgnoreRequest,
  TranscriptResponse,
  AcceptAllSuggestionsRequest,
  AcceptAllSuggestionsResult,
  AutoCommitRequest,
  AutoCommitResult,
  RDClassificationOverrideRequest,
  RDClassificationOverrideResponse,
  ProposalListResponse,
  ProposalDetail,
  AcceptProposalRequest,
  AcceptProposalResponse,
  RejectProposalRequest,
  EditProposalRequest,
  MergeProposalResponse,
  ProposalStatus,
} from './types-import';

// ─── Generic fetch helper (matches projects.ts shape) ──────────────────────

interface UseApiState<T> {
  data:    T | null;
  loading: boolean;
  error:   ApiError | null;
  refetch: () => Promise<void>;
}

/**
 * Flatten an axios/fetch error into the legacy ApiError shape that the
 * staged list / mapping / commit / ignore hooks still consume. New code
 * that wants the rich envelope (correlation id, technical details, copy-
 * report button) should use {@link toApiErrorEnvelope} instead.
 *
 * The "message" extraction is intentionally tolerant of three shapes:
 *   1. The new ApiErrorEnvelope from ApiExceptionFilter — uses
 *      response.data.error.message
 *   2. The previous string-only error shape — uses response.data.error
 *      or response.data.message
 *   3. A bare network/axios error — uses err.message
 */
function toApiError(err: any): ApiError {
  // New envelope first — extract the human-facing message field.
  const envMessage =
    err?.response?.data?.error?.message
    && typeof err.response.data.error.message === 'string'
      ? (err.response.data.error.message as string)
      : null;

  return {
    message: envMessage
          ?? err?.response?.data?.error
          ?? err?.response?.data?.message
          ?? err?.message
          ?? 'Request failed',
    status:  err?.response?.status ?? 500,
    errors:  err?.response?.data?.errors,
  };
}

/**
 * Extract the rich {@link ApiErrorEnvelope} from a thrown axios/fetch error.
 * Falls back to a synthetic envelope built from whatever info we have, so
 * the UI always has something structured to render via &lt;ErrorPanel&gt;.
 *
 * Network drops, CORS failures, and timeouts all land in the synthetic
 * branch — the user still sees a panel with a correlation id (a synthetic
 * client-side one prefixed with "client_") and a copyable bug report.
 */
function toApiErrorEnvelope(err: any, fallbackTitle = 'Request failed'): ApiErrorEnvelope {
  // axios shape: response.data is the parsed body
  const body = err?.response?.data;
  if (body && isApiErrorEnvelope(body)) {
    return body;
  }

  // Some clients re-throw the parsed body directly
  if (isApiErrorEnvelope(err)) {
    return err;
  }

  // Synthetic — build from whatever we have
  return buildClientErrorEnvelope(err, fallbackTitle);
}

// ─── Preview (no fetch hook — preview is mutation-shaped) ──────────────────
//
// Preview is a POST with a file body, so it doesn't fit the
// fetch-on-mount pattern. We expose it as a manual trigger that the
// UploadZone calls when the user picks a file.

export function useImportPreview() {
  const { createApi } = useApiClient();
  const [data,     setData]     = useState<ImportPreviewResponse | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<ApiError | null>(null);
  // Parallel rich-error field. The legacy `error` is kept for any caller
  // that still wants the flat shape; new UI consumes `envelope` and feeds
  // it directly into <ErrorPanel>.
  const [envelope, setEnvelope] = useState<ApiErrorEnvelope | null>(null);

  const preview = useCallback(
    async (file: File): Promise<ImportPreviewResponse | null> => {
      setLoading(true);
      setError(null);
      setEnvelope(null);
      try {
        const api = await createApi();
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await api.post<ImportPreviewResponse>(
          '/api/v1/import/claude-export/preview',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            // Large exports take a while to parse — bump the per-request timeout
            timeout: 5 * 60 * 1000,
          }
        );
        setData(response.data);
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        setEnvelope(toApiErrorEnvelope(err, 'Preview failed'));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setEnvelope(null);
  }, []);

  return { preview, data, loading, error, envelope, reset };
}

// ─── Confirm import (POST /api/v1/import/claude-export) ───────────────────
//
// 2026.4.9 Wave 3: now returns a discriminated result that distinguishes
// between a successful 202 and a 409 IMPORT_IN_FLIGHT. The 409 is NOT an
// error from the user's perspective — it just means another import is
// running. Callers feed `conflict` into setInFlightFromConflict() so the
// banner appears, instead of routing to ErrorPanel.

export interface ImportClaudeExportOutcome {
  result?:   ImportDispatchResult;
  conflict?: ImportInFlightConflict;
}

export function useImportClaudeExport() {
  const { createApi } = useApiClient();
  const [data,     setData]     = useState<ImportDispatchResult | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<ApiError | null>(null);
  const [envelope, setEnvelope] = useState<ApiErrorEnvelope | null>(null);

  const importExport = useCallback(
    async (file: File): Promise<ImportClaudeExportOutcome | null> => {
      setLoading(true);
      setError(null);
      setEnvelope(null);
      try {
        const api = await createApi();
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await api.post<ImportDispatchResult>(
          '/api/v1/import/claude-export',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10 * 60 * 1000,
            // Don't throw on 409 — we want to inspect the body and surface
            // it as a conflict outcome rather than an error envelope. Any
            // other 4xx/5xx still throws into the catch below.
            validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
          }
        );

        if (response.status === 409) {
          // Server says another import is already in flight. The body shape
          // is { code: 'IMPORT_IN_FLIGHT', batchId, state, message } —
          // surface it to the caller without polluting `data` or `error`.
          const conflict = response.data as unknown as ImportInFlightConflict;
          return { conflict };
        }

        setData(response.data);
        return { result: response.data };
      } catch (err: any) {
        setError(toApiError(err));
        setEnvelope(toApiErrorEnvelope(err, 'Import failed'));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setEnvelope(null);
  }, []);

  return { importExport, data, loading, error, envelope, reset };
}

// ─── Staged list (GET /api/v1/import/staged) ──────────────────────────────

export function useStagedConversations(
  request: StagedListRequest,
  enabled = true
): UseApiState<StagedListResponse> {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<StagedListResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error,   setError]   = useState<ApiError | null>(null);

  // Stable JSON key for the dependency array — request is an object literal
  // so referential equality would re-fire on every render.
  const requestKey = JSON.stringify(request);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const params = new URLSearchParams();
      if (request.status)      params.set('status',      request.status);
      if (request.sourceType)  params.set('sourceType',  request.sourceType);
      if (request.batchId)     params.set('batchId',     request.batchId);
      if (request.page)        params.set('page',        String(request.page));
      if (request.pageSize)    params.set('pageSize',    String(request.pageSize));
      if (request.needsReview) params.set('needsReview', 'true');

      const url = params.toString()
        ? `/api/v1/import/staged?${params}`
        : '/api/v1/import/staged';

      const response = await api.get<StagedListResponse>(url);
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createApi, requestKey, enabled]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Single staged row (GET /api/v1/import/staged/{id}) ───────────────────

export function useStagedDetail(
  id: number | null
): UseApiState<StagedConversationResponse> {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<StagedConversationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    if (id == null || id <= 0) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const response = await api.get<StagedConversationResponse>(
        `/api/v1/import/staged/${id}`
      );
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [createApi, id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Mapping update (PUT /api/v1/import/staged/{id}/mapping) ──────────────

export function useUpdateMapping() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const update = useCallback(
    async (
      id: number,
      request: UpdateMappingRequest
    ): Promise<StagedConversationResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.put<StagedConversationResponse>(
          `/api/v1/import/staged/${id}/mapping`,
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { update, loading, error };
}

// ─── Commit (POST /api/v1/import/commit) ──────────────────────────────────

export function useCommitImport() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const commit = useCallback(
    async (request: CommitRequest): Promise<CommitResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<CommitResult>(
          '/api/v1/import/commit',
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { commit, loading, error };
}

// ─── Ignore (POST /api/v1/import/staged/{id}/ignore) ──────────────────────

export function useIgnoreStaged() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const ignore = useCallback(
    async (id: number, request: IgnoreRequest = {}): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        await api.post(`/api/v1/import/staged/${id}/ignore`, request);
        return true;
      } catch (err: any) {
        setError(toApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { ignore, loading, error };
}

// ─── Transcript (GET /api/v1/import/staged/{id}/transcript) ──────────────

export function useTranscript(id: number | null): UseApiState<TranscriptResponse> {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<TranscriptResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    if (id == null || id <= 0) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const response = await api.get<TranscriptResponse>(
        `/api/v1/import/staged/${id}/transcript`
      );
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [createApi, id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Accept AI suggestion (POST /api/v1/import/staged/{id}/accept-suggestion)

export function useAcceptSuggestion() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const accept = useCallback(
    async (id: number): Promise<StagedConversationResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<StagedConversationResponse>(
          `/api/v1/import/staged/${id}/accept-suggestion`
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { accept, loading, error };
}

// ─── Manual R&D override (POST /api/v1/import/staged/{id}/rd-override) ────
//
// Used from the StagedDetail drawer when a human reviewer resolves a
// needs-review row (AIQualifiesForRDCredit=null after the null-on-suspicious
// safety net tripped) or corrects a clearly-wrong AI verdict. The override
// is audit-logged on the server; callers just need to surface any error
// back to the UI.

export function useRDClassificationOverride() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const override = useCallback(
    async (
      id: number,
      request: RDClassificationOverrideRequest
    ): Promise<RDClassificationOverrideResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<RDClassificationOverrideResponse>(
          `/api/v1/import/staged/${id}/rd-override`,
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { override, loading, error };
}

// ─── Accept all AI suggestions (POST /api/v1/import/accept-all-suggestions)

export function useAcceptAllSuggestions() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const acceptAll = useCallback(
    async (request: AcceptAllSuggestionsRequest = {}): Promise<AcceptAllSuggestionsResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<AcceptAllSuggestionsResult>(
          '/api/v1/import/accept-all-suggestions',
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { acceptAll, loading, error };
}

// ─── Auto-commit (POST /api/v1/import/auto-commit) ────────────────────────

export function useAutoCommit() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const autoCommit = useCallback(
    async (request: AutoCommitRequest = {}): Promise<AutoCommitResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<AutoCommitResult>(
          '/api/v1/import/auto-commit',
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { autoCommit, loading, error };
}

// ─── Catalog Health ────────────────────────────────────────────────────────

export function useCatalogHealth() {
  const { createApi } = useApiClient();
  const [data, setData] = useState<import('./types-import').CatalogHealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const api = await createApi();
      const res = await api.get('/api/v1/import/catalog-health');
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data ?? { error: err.message });
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}


// ═══════════════════════════════════════════════════════════════════════════
// Proposed work items (AI-proposed work items review queue)
// ═══════════════════════════════════════════════════════════════════════════
//
// See ProposalsController.cs for the server-side endpoints. The review
// flow is: list pending proposals → expand to inspect supporting
// conversations → accept (creates WorkItem + back-fills contributing
// conversations so auto-commit can pick them up) OR reject OR edit OR
// merge-into-another.

// ─── List (GET /api/v1/import/proposals) ──────────────────────────────────

export function useProposals(
  request: { batchId?: string; status?: ProposalStatus | 'All' },
  enabled = true
): UseApiState<ProposalListResponse> {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<ProposalListResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error,   setError]   = useState<ApiError | null>(null);

  const requestKey = JSON.stringify(request);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const params = new URLSearchParams();
      if (request.batchId) params.set('batchId', request.batchId);
      if (request.status)  params.set('status',  request.status);

      const url = params.toString()
        ? `/api/v1/import/proposals?${params}`
        : '/api/v1/import/proposals';

      const response = await api.get<ProposalListResponse>(url);
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createApi, requestKey, enabled]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Detail (GET /api/v1/import/proposals/{id}) ───────────────────────────

export function useProposalDetail(
  id: number | null
): UseApiState<ProposalDetail> {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<ProposalDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    if (id == null) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const response = await api.get<ProposalDetail>(`/api/v1/import/proposals/${id}`);
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [createApi, id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Accept (POST /api/v1/import/proposals/{id}/accept) ───────────────────

export function useAcceptProposal() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const accept = useCallback(
    async (
      id: number,
      request: AcceptProposalRequest
    ): Promise<AcceptProposalResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<AcceptProposalResponse>(
          `/api/v1/import/proposals/${id}/accept`,
          request
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { accept, loading, error };
}

// ─── Reject (POST /api/v1/import/proposals/{id}/reject) ───────────────────

export function useRejectProposal() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const reject = useCallback(
    async (id: number, request: RejectProposalRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        await api.post(`/api/v1/import/proposals/${id}/reject`, request);
        return true;
      } catch (err: any) {
        setError(toApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { reject, loading, error };
}

// ─── Edit (PUT /api/v1/import/proposals/{id}) ─────────────────────────────

export function useEditProposal() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const edit = useCallback(
    async (id: number, request: EditProposalRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        await api.put(`/api/v1/import/proposals/${id}`, request);
        return true;
      } catch (err: any) {
        setError(toApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { edit, loading, error };
}

// ─── Merge (POST /api/v1/import/proposals/{id}/merge-into/{targetId}) ─────

export function useMergeProposal() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const merge = useCallback(
    async (
      sourceId: number,
      targetId: number
    ): Promise<MergeProposalResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<MergeProposalResponse>(
          `/api/v1/import/proposals/${sourceId}/merge-into/${targetId}`
        );
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { merge, loading, error };
}
